import { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { useScreenClass } from "react-grid-system";
import Card from "../../components/Card";
import Button from "../../components/Button";
import GradeCard from "../../components/GradeCard";
import Breadcrumb from "../../components/Breadcrumb";
import Icon from "../../components/Icon";
import { Col, Row } from "../../components/StyledGridSystem";
import Status from "../../types/enum/Status";
import FlexBox, { FlexGrow } from "../../components/FlexBox";
import { NullStateText } from "../../components/Text";
import { StudentContext } from "../../context/StudentBehalfContext";
import Alert from "../../components/Alert";
import SnackBar from "../../components/SnackBar";
import MinimalSelect from "../../components/MinimalSelect";
import Label from "../../components/Label";
import { set } from "react-hook-form";
import { DocDownloadsModal } from "../courses/DocDownloadsModal";
import { useLocation } from "react-router-dom";

const ZeroCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing.x3};
  margin-top: ${({ theme }) => theme.spacing.x6};
  text-align: center;
`;

interface report {
  report: any;
  summary: any;
}

function StudentHistory(props: any) {
  const location = useLocation();

  const search = new URLSearchParams(location?.search);
  const first_name = search.get("first_name") || "";
  const userId = search.get("userId") || "";
  const stuUserId = search.get("stu_user_id") || "";
  const name = search.get("name") || "";
  // const { userId } = props.location.state || {};
  // const { name } = props.location.state || {};
  // const { first_name } = props.location.state || {};
  const { student, isBehalf } = useContext(StudentContext);

  const [loading, setLoading] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [isErrorOpen, setIsErrorOpen] = useState<boolean>(false);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [type, setType] = useState<"info" | "error" | "success" | "warning">(
    "info"
  );
  const [enrolled, setEnrolled] = useState<boolean>(false);

  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<{
    label: string;
    value: string;
    thesis_title?: string;
  }>({ label: "", value: "", thesis_title: "" });
  const [programOption, setProgramOption] = useState([
    { label: "", value: "" },
  ]);
  const [openDocDownloads, setOpenDocDownloads] = useState(false);

  const screenClass = useScreenClass();
  const isMobile = useMemo(() => ["xs", "sm"].includes(screenClass), [
    screenClass,
  ]);
  const [history, setHistory] = useState<report>({
    report: [],
    summary: {},
  });
  const [profileId, setProfileId] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(false);

  const sharedMinimalStyle = css`
    min-width: 200px;
    border-color: hsl(0, 0%, 80%);
    border-radius: 4px;
    border-style: solid;
    border-width: 1px;
    padding: 0px 12px;
    .react-select__menu {
      text-align: left;
      width: 100%;
      min-width: unset;
      margin-left: -12px;
    }
  `;
  const StyledMinimalSelect = styled(MinimalSelect)`
    ${sharedMinimalStyle}
  `;
  // <-------- fetch data ---------->
  useEffect(() => {
    fetchGradeHistory(userId);

    // Call the function to fetch programs and handbooks
    getProgramsAndHandbooks()
      ?.then((data) => {
        setPrograms(data);
      })
      .catch(() => {});
  }, [selectedProgram.value]);

  const fetchGradeHistory = (stdid: string) => {
    setLoading(false);
    selectedProgram.value &&
      axios
        .get(
          `/api/v1/students/grades?student_id=${userId}&program_id=${selectedProgram.value}`
        )
        .then((res) => {
          setLoading(true);
          setHistory(res.data.data);
          if (res.data.data !== undefined && stdid) {
            setEnrolled(true);
          }
        })
        .catch((err) => {
          setLoading(true);
        });
  };

  const fetchVerificationLetter = () => {

    document.body.classList.add("loading-cursor");
    setDisabled(true);
    setType("info");
    setIsSnackBarOpen(true);
    setMessage("Downloading verification letter...");
    axios
      .get(
        `/api/v1/students/enrollment?student_id=${userId}&program_id=${selectedProgram.value}`,
        {
          responseType: "blob",
        }
      )
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `verification_letter_${first_name}.pdf`);
        document.body.appendChild(link);
        link.click();
        setType("success");
        setIsSnackBarOpen(true);
        setMessage("Verification letter downloaded successfully!");
      })
      .catch((err) => {
        setLoading(true);
        setType("error");
        setIsSnackBarOpen(true);
        setMessage(
          "Failed to download the verification letter. Please try again."
        );
      })
      .finally(() => {
        document.body.classList.remove("loading-cursor");
        setDisabled(false);
      });
  };

  const printOfficialTranscript = async () => {
    const w = window.open("", "_blank");
    if (!w) {
      setIsErrorOpen(true);
      setMessage("Please allow pop-ups to print the document.");
      return;
    }

    w.document.write("Generating document… Please wait.");

    try {
      const res = await fetch(
        `/api/v1/students/transcript/v2?student_id=${userId}&program_id=${selectedProgram.value}&status=official&do_print=true`
      );

      if (!res.ok) {
        w.close();
        setIsErrorOpen(true);
        setMessage("Failed to generate the document.");
        return;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      w.location.href = url;

      setTimeout(() => {
        w.focus();
        w.print();
      }, 800);
    } catch (e) {
      console.error(e);
      w.close();
      setIsErrorOpen(true);
      setMessage("Unexpected error occurred.");
    }
  };

  const fetchUnofficialTranscript = () => {
    document.body.classList.add("loading-cursor");
    setDisabled(true);
    setType("info");
    setIsSnackBarOpen(true);
    setMessage("Downloading unofficial transcript....");
    axios
      .get(
        `/api/v1/students/transcript/v2?student_id=${userId}&program_id=${selectedProgram.value}&status=unofficial`,
        {
          responseType: "blob",
        }
      )
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `unofficial_transcript_${first_name}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        setIsSnackBarOpen(true);
        setType("success");
        setMessage("Unofficial transcirpt downloaded successfully!");
      })
      .catch((err) => {
        setLoading(true);
        setType("error");
        setIsSnackBarOpen(true);
        setMessage(
          "Failed to download the unofficial transcript. Please try again."
        );
      })
      .finally(() => {
        document.body.classList.remove("loading-cursor");
        setDisabled(false);
      });
  };
  const fetchOfficialTranscript = () => {
    document.body.classList.add("loading-cursor");
    setDisabled(true);
    setType("info");
    setIsSnackBarOpen(true);
    setMessage("Downloading official transcript....");
    axios
      .get(
        `/api/v1/students/transcript/v2?student_id=${userId}&program_id=${selectedProgram.value}&status=official`,
        {
          responseType: "blob",
        }
      )
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `official_transcript_${first_name}.pdf`);
        document.body.appendChild(link);
        link.click();
        setIsSnackBarOpen(true);
        setType("success");
        setMessage("Official transcript downloaded successfully!");
      })
      .catch((err) => {
        setLoading(true);
        setIsSnackBarOpen(true);
        setType("error");
        setMessage(
          "Failed to download the official transcript. Please try again."
        );
      })
      .finally(() => {
        document.body.classList.remove("loading-cursor");
        setDisabled(false);
      });
  };

  const fetchCertificateOfStatus = () => {
     document.body.classList.add("loading-cursor");
    setDisabled(true);
    setType("info");
    setIsSnackBarOpen(true);
    setMessage("Downloading certificate of student status....");
    axios
      .get(
        `/api/v1/students/cert-of-student-status?user_id=${stuUserId}&program_id=${selectedProgram.value}`,
        {
          responseType: "blob",
        }
      )
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `certificate_of_student_status_${first_name}.pdf`);
        document.body.appendChild(link);
        link.click();
        setIsSnackBarOpen(true);
        setType("success");
        setMessage("Certificate of Student Status downloaded successfully!");
      })
      .catch((err) => {
        setLoading(true);
        setIsSnackBarOpen(true);
        setType("error");
        setMessage(
          "Failed to download the certificate of student status. Please try again."
        );
      })
      .finally(() => {
        document.body.classList.remove("loading-cursor");
        setDisabled(false);
      });
  }
  interface Program {
    id: string;
    name: string;
    short_name: string;
    handbook: Handbook;
  }

  interface Handbook {
    url: string;
    file_name: string;
    description: string;
  }

  async function fetchPrograms(): Promise<Program[]> {
    try {
      const res = await axios.get<{ data: { programs: Program[] } }>(
        "/api/v1/courses/programs"
      );
      return res.data.data.programs;
    } catch (error) {
      console.error("Error fetching programs:", error);
      return [];
    }
  }
  async function fetchUserProgramInfo(id: string): Promise<any> {
    try {
      const res = await axios.get<{ data: { programs: any; user_id: string } }>(
        `/api/v1/students/${id}`
      );
      if (res.data.data.programs.length > 0) {
        const tempPrograms: Array<{
          label: string;
          value: string;
          thesis_title?: string;
        }> = [];
        !selectedProgram.value &&
          setSelectedProgram({
            label:
              res.data.data.programs[0]?.short_name +
              " (" +
              res.data.data.programs[0].activity_type_name +
              ")",
            value: res.data.data.programs[0]?.program_id,
            thesis_title: res.data.data.programs[0].thesis_title,
          });

        res.data.data.programs.map(
          (program: {
            program_id: string;
            short_name: string;
            activity_type_name: string;
            thesis_title?: string;
          }) =>
            tempPrograms.push({
              label:
                program.short_name + " (" + program.activity_type_name + ")",
              value: program.program_id,
              thesis_title: program?.thesis_title,
            })
        );
        setProgramOption(tempPrograms);
        setProfileId(res.data.data.user_id);
      }
      return res.data.data.programs;
    } catch (error: any) {
      return Promise.reject("Failed to return fetchUserProgramInfo!");
    }
  }

  async function fetchHandbook(prog: string): Promise<Handbook> {
    try {
      const res = await axios.get<{ data: { document: Handbook } }>(
        `/api/v1/document/program/${prog}`
      );
      return res.data.data.document;
    } catch (error: any) {
      console.error("Error fetching handbook:", error);
      // setMessage(
      //   error.response?.data?.error?.message || "Failed to find handbook!"
      // );
      // setIsErrorOpen(true);
      setLoading(false);
      return { url: "", file_name: "", description: "" }; // Return blank data
    }
  }

  async function getProgramsAndHandbooks(): Promise<Program[]> {
    try {
      const studentProgram = await fetchUserProgramInfo(userId);
      const programs = await fetchPrograms();
      let wantedPrograms = selectedProgram.value
        ? programs.filter((p: Program) => p.id === selectedProgram.value)
        : programs.filter(
            (p: Program) => p.id === studentProgram?.[0]?.program_id
          ) || [];
      const programsWithHandbooks = await Promise.all(
        wantedPrograms.map(async (program) => {
          const handbook = await fetchHandbook(program.short_name);
          return {
            ...program,
            handbook,
          };
        })
      );
      return programsWithHandbooks;
    } catch (error) {
      console.error("Error fetching programs or handbooks:", error);
      return Promise.reject("Error fetching programs or handbooks!");
    }
  }

  // <-------- mock data ---------->
  const test = [
    {
      name: name,
      link: {
        pathname: "/personalInfo",
        state: {
          userId: profileId,
          role: "admin",
        },
      },
    },
    { name: "Enrollment History", link: "/courses/enroll-history" },
  ];

  return (
    <>
      <DocDownloadsModal
        printOfficialTranscript={printOfficialTranscript}
        fetchVerificationLetter={fetchVerificationLetter}
        fetchOfficialTranscript={fetchOfficialTranscript}
        fetchUnofficialTranscript={fetchUnofficialTranscript}
        fetchCertificateOfStatus={fetchCertificateOfStatus}
        isOpen={openDocDownloads}
        setOpen={setOpenDocDownloads}
      />
      <SnackBar
        isOpen={isErrorOpen}
        setOpen={setIsErrorOpen}
        message={message}
        type="error"
      />
      <SnackBar
        isOpen={isSnackBarOpen}
        setOpen={setIsSnackBarOpen}
        message={message}
        type={type}
      />
      <Row>
        <Col>
          <Breadcrumb path={test} />
        </Col>
        <Col style={{ textAlign: "end" }}>
          <StyledMinimalSelect
            value={programOption.find(
              (data) => data.value === selectedProgram.value
            )}
            onChange={async (data: { label: string; value: string }) =>
              setSelectedProgram(data)
            }
            options={
              programOption.sort((a: any, b: any) =>
                a.label?.localeCompare(b.label)
              ) || []
            }
          />
        </Col>
      </Row>
      {/* <Row> */}
      <Col>
        <FlexBox space={2}>
          <FlexGrow>
            <h2 style={{ marginRight: "auto", marginBottom: 0 }}>
              Enrollment History &amp; Grades
            </h2>
          </FlexGrow>
        </FlexBox>
      </Col>
      {/* </Row> */}
      {selectedProgram?.thesis_title && (
        <div style={{ marginBottom: 20 }}>
          <span style={{ fontSize: 18, fontWeight: 600, color: "#5d5d5d" }}>
            Thesis Title :{" "}
          </span>{" "}
          <span style={{ fontSize: 20, fontWeight: 700, fontStyle: "italic" }}>
            {" "}
            {selectedProgram?.thesis_title}
          </span>
        </div>
      )}
      <Row>
        <Col>
          <Button
            isOutline
            status={Status.primary}
            // style={{ marginLeft: "auto" }}
            onClick={() => setOpenDocDownloads(true)}
            disabled={!enrolled}
          >
            <Icon mr={1} name="download" /> Download transcripts and official
            documents
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <Alert
            style={{
              backgroundColor: "#e3f1fd",
              color: "#1c5f9d",
              border: "2px solid #1c5f9d",
            }}
          >
            {!isMobile &&
              programs[0]?.handbook?.url &&
              typeof programs[0] === "object" && (
                <FlexBox>
                  <FlexGrow>{programs[0]?.handbook.description}</FlexGrow>
                  <div>
                    <a
                      style={{ color: "#1c5f9d" }}
                      target="_blank"
                      rel="noopener noreferrer"
                      href={programs[0].handbook.url}
                    >
                      Download {programs[0].name} Handbook{" "}
                      <Icon mr={1} name="chevron-right-s" />
                    </a>
                  </div>
                </FlexBox>
              )}
            {isMobile && programs[0] && typeof programs[0] === "object" && (
              <div>
                <div>{programs[0]?.handbook.description}</div>
                <div>
                  <a
                    style={{ color: "#1c5f9d" }}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={programs[0]?.handbook.url}
                  >
                    Download {programs[0]?.name} Handbook{" "}
                    <Icon mr={1} name="chevron-right-s" />
                  </a>
                </div>
              </div>
            )}
          </Alert>
        </Col>
      </Row>

      {enrolled ? (
        <>
          {history.report.map((data: any, index: number) => (
            <Row key={index}>
              <Col key={index}>
                <>
                  <GradeCard
                    key={index}
                    isMobile={isMobile}
                    termId={data.term_id}
                    semester={data.semester}
                    info={data.grades}
                    isBehalf={isBehalf}
                    summary={history.summary.summary[data.semester]}
                    studentId={userId}
                    cumulative={history.summary.cumulative[data.semester]}
                  />
                </>
              </Col>
            </Row>
          ))}
        </>
      ) : (
        <Col>
          <ZeroCard>
            <NullStateText>No enrollment history.</NullStateText>
            {/* <FlexBox direction="column" space={2}>
              <h2>Sorry, you didn't enroll any course.</h2>
              <p>Please enroll the course to view the grades</p>
              <Link to="/courses/myCourses">Enroll now</Link>
            </FlexBox> */}
          </ZeroCard>
        </Col>
      )}
      {disabled && (
        <div className="overlay">
          <div className="overlay-content"></div>
        </div>
      )}
    </>
  );
}
export default StudentHistory;
